import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  clearLocalLegacyDrafts,
  deleteDraft,
  downloadTemplateJson,
  duplicateDraft,
  loadBuiltInTemplates,
  loadDrafts,
  loadLocalLegacyDrafts,
  migrateLocalDraftsToCloud,
  parseImportedTemplate,
  renameDraft,
  resetBuiltInOverride,
  withFreshBlockIds,
  type Template,
} from '../lib/templates';
import { formatTimestamp } from '../lib/format';

export function TemplatesPage() {
  const navigate = useNavigate();
  const onLoadTemplate = (t: Template) => {
    navigate('/compose', {
      state: { template: { ...t, blocks: withFreshBlockIds(t.blocks) } },
    });
  };

  const [drafts, setDrafts] = useState<Template[]>([]);
  const [builtIn, setBuiltIn] = useState<Array<Template & { _overridden?: boolean }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [legacyDrafts, setLegacyDrafts] = useState<Template[]>([]);
  const [migrating, setMigrating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    refresh();
    setLegacyDrafts(loadLocalLegacyDrafts());
  }, []);

  async function refresh() {
    setBuiltIn(loadBuiltInTemplates());
    try {
      setDrafts(await loadDrafts());
    } catch (e) {
      setError(`Failed to load drafts: ${(e as Error).message}`);
    }
  }

  function onReset(id: string, name: string) {
    if (!confirm(`Reset "${name}" to its default? Your local edits to this template will be lost.`)) return;
    resetBuiltInOverride(id);
    refresh();
  }

  async function onRename(t: Template) {
    const name = prompt('New name for this draft:', t.name);
    if (!name || name.trim() === t.name) return;
    try {
      await renameDraft(t, name.trim());
      await refresh();
    } catch (e) {
      setError(`Rename failed: ${(e as Error).message}`);
    }
  }

  async function onDuplicate(t: Template) {
    try {
      await duplicateDraft(t);
      await refresh();
    } catch (e) {
      setError(`Duplicate failed: ${(e as Error).message}`);
    }
  }

  async function onDelete(id: string) {
    if (!confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await deleteDraft(id);
      await refresh();
    } catch (e) {
      setError(`Delete failed: ${(e as Error).message}`);
    }
  }

  async function onMigrate() {
    setMigrating(true);
    setError(null);
    try {
      const r = await migrateLocalDraftsToCloud();
      setLegacyDrafts(loadLocalLegacyDrafts());
      await refresh();
      if (r.failed > 0) {
        setError(`Migrated ${r.migrated}, ${r.failed} failed — local copies kept.`);
      }
    } catch (e) {
      setError(`Migration failed: ${(e as Error).message}`);
    } finally {
      setMigrating(false);
    }
  }

  function onDismissLegacy() {
    if (!confirm(`Delete ${legacyDrafts.length} local draft${legacyDrafts.length === 1 ? '' : 's'} without uploading? This cannot be undone.`)) return;
    clearLocalLegacyDrafts();
    setLegacyDrafts([]);
  }

  function onImportClick() {
    setError(null);
    fileInputRef.current?.click();
  }

  async function onImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const t = parseImportedTemplate(text);
      onLoadTemplate(t);
    } catch (err) {
      setError(`Import failed: ${(err as Error).message}`);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <>
      {error && <div className="error">{error}</div>}

      {legacyDrafts.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--accent, #0ea5e9)' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>
            {legacyDrafts.length} local draft{legacyDrafts.length === 1 ? '' : 's'} found
          </h3>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            Drafts now live in the cloud so you can use them across devices. Upload your
            local copies to keep them, or discard them if no longer needed.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button className="primary" onClick={onMigrate} disabled={migrating}>
              {migrating ? 'Uploading…' : `Upload ${legacyDrafts.length} to cloud`}
            </button>
            <button className="secondary" onClick={onDismissLegacy} disabled={migrating}>
              Discard local
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Saved drafts</h3>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
              Stored in the cloud per signed-in admin. Export to JSON to commit as a built-in template.
            </p>
          </div>
          <button className="secondary" onClick={onImportClick}>Import JSON…</button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={onImportFile}
          />
        </div>

        {drafts.length === 0 ? (
          <p className="muted" style={{ marginTop: 16 }}>
            No drafts yet. Compose an email and click <strong>Save as draft</strong>.
          </p>
        ) : (
          <ul className="template-list">
            {drafts.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                onLoad={() => onLoadTemplate(t)}
                onExport={() => downloadTemplateJson(t)}
                onRename={() => onRename(t)}
                onDuplicate={() => onDuplicate(t)}
                onDelete={() => onDelete(t.id)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <h3 style={{ margin: 0, fontSize: 16 }}>Built-in templates</h3>
        <p className="muted" style={{ margin: '4px 0 16px', fontSize: 12 }}>
          Loaded from <code>src/templates/*.json</code>. To add one, drop a JSON file there and rebuild.
        </p>
        {builtIn.length === 0 ? (
          <p className="muted">No built-in templates.</p>
        ) : (
          <ul className="template-list">
            {builtIn.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                onLoad={() => onLoadTemplate(t)}
                onExport={() => downloadTemplateJson(t)}
                onReset={t._overridden ? () => onReset(t.id, t.name) : undefined}
                builtIn
                overridden={t._overridden}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function TemplateRow({
  template,
  onLoad,
  onExport,
  onRename,
  onDuplicate,
  onDelete,
  onReset,
  builtIn,
  overridden,
}: {
  template: Template;
  onLoad: () => void;
  onExport: () => void;
  onRename?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onReset?: () => void;
  builtIn?: boolean;
  overridden?: boolean;
}) {
  return (
    <li className="template-row">
      <div className="template-meta">
        <strong>{template.name}</strong>
        {template.description && <span className="muted"> — {template.description}</span>}
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          {template.blocks.length} block{template.blocks.length === 1 ? '' : 's'}
          {template.subject && <> · subject: <em>{template.subject}</em></>}
          {template.createdAt && <> · saved {formatTimestamp(template.createdAt)}</>}
          {builtIn && <> · <span className="badge subscribed">built-in</span></>}
          {overridden && <> · <span className="badge unsubscribed">modified locally</span></>}
        </div>
      </div>
      <div className="row" style={{ gap: 6 }}>
        <button className="primary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onLoad}>
          Load
        </button>
        <button className="secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onExport}>
          Export
        </button>
        {onRename && (
          <button className="secondary" style={{ padding: '6px 10px', fontSize: 13 }} onClick={onRename}>
            Rename
          </button>
        )}
        {onDuplicate && (
          <button className="secondary" style={{ padding: '6px 10px', fontSize: 13 }} onClick={onDuplicate}>
            Duplicate
          </button>
        )}
        {onReset && (
          <button className="secondary" style={{ padding: '6px 10px', fontSize: 13, color: 'var(--warning)' }} onClick={onReset}>
            Reset
          </button>
        )}
        {onDelete && (
          <button className="secondary" style={{ padding: '6px 10px', fontSize: 13, color: 'var(--danger)' }} onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </li>
  );
}
